import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Ban, CheckCircle2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useClient, useSaveClient } from "./api";
import { ClientForm } from "./ClientForm";
import { ContactsTab } from "./tabs/ContactsTab";
import { PricesTab } from "./tabs/PricesTab";
import { ActivityTab } from "./tabs/ActivityTab";
import { ClientQuotesTab, ClientOrdersTab, ClientContractsTab } from "./tabs/QuotesOrdersTabs";
import { ClientLeadsTab } from "./tabs/LeadsTab";
import { CLIENT_STATUS_LABEL, DELIVERY_CHANNEL_LABEL } from "@/types";
import { ago, dateShort } from "@/lib/format";
import { CustomFieldsView } from "@/components/custom-fields/CustomFieldsEditor";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { DeleteButton } from "@/components/shared/DeleteDialog";
import { PurgeTestClientButton } from "@/modules/admin/PurgeTestClient";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClientInvoicesTab } from "./tabs/InvoicesTab";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_VARIANT = { active: "default", suspended: "destructive", closed: "secondary" } as const;

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/60 py-1.5 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{value ?? "—"}</dd>
    </div>
  );
}

export default function ClientPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { data: client, isLoading } = useClient(id);
  const save = useSaveClient();
  const [editing, setEditing] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [reason, setReason] = useState("");

  if (isLoading) return <p className="text-sm text-muted-foreground">Chargement…</p>;
  if (!client) return <p className="text-sm text-destructive">Client introuvable.</p>;

  const cfg = (client.delivery_config as Record<string, unknown> | null) ?? {};

  function suspend() {
    if (!reason.trim()) { toast.error("Indique un motif."); return; }
    save.mutate({ id: client!.id, values: { status: "suspended", suspended_reason: reason.trim() } }, { onSuccess: () => { setSuspending(false); setReason(""); } });
  }
  function unsuspend() {
    save.mutate({ id: client!.id, values: { status: "active" } });
  }

  return (
    <div>
      <Link to="/clients" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Clients</Link>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="break-words font-heading text-2xl font-bold">{client.legal_name}</h1>
            <Badge variant={STATUS_VARIANT[client.status]}>{CLIENT_STATUS_LABEL[client.status]}</Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {client.trade_name ? `${client.trade_name} · ` : ""}{client.country} · {client.currency} · {client.last_activity_at ? `dernière activité ${ago(client.last_activity_at)}` : "aucune activité enregistrée"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {client.status === "suspended" ? (
            <Button variant="outline" onClick={unsuspend}><CheckCircle2 className="mr-2 h-4 w-4" />Lever la suspension</Button>
          ) : (
            <Button variant="outline" onClick={() => setSuspending(true)}><Ban className="mr-2 h-4 w-4" />Suspendre les livraisons</Button>
          )}
          <Button onClick={() => setEditing(true)}><Pencil className="mr-2 h-4 w-4" />Modifier</Button>
          <DeleteButton entity="client" id={client.id} variant="outline" onDeleted={() => nav("/clients")} onArchived={() => nav("/clients")} />
          {client.stripe_mode === "test" && <PurgeTestClientButton clientId={client.id} name={client.legal_name} onDone={() => nav("/clients")} />}
        </div>
      </div>

      {client.status === "suspended" && (
        <div className="mb-4 rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm">
          <span className="font-semibold text-destructive">Livraisons suspendues</span> depuis le {dateShort(client.suspended_at)}
          {client.suspended_reason && <> — {client.suspended_reason}</>}. Aucune livraison ne peut être enregistrée tant que la suspension est active.
        </div>
      )}

      <Tabs defaultValue="summary">
        <TabsList className="mb-4 h-auto flex-wrap justify-start gap-1">
          <TabsTrigger value="summary">Résumé</TabsTrigger>
          <TabsTrigger value="contacts">Interlocuteurs</TabsTrigger>
          <TabsTrigger value="prices">Tarifs négociés</TabsTrigger>
          <TabsTrigger value="activity">Activité & tâches</TabsTrigger>
          <TabsTrigger value="quotes">Devis</TabsTrigger>
          <TabsTrigger value="contracts">Contrats</TabsTrigger>
          <TabsTrigger value="orders">Commandes</TabsTrigger>
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="invoices">Factures & paiements</TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <div className="grid gap-6 lg:grid-cols-2">
            <section className="glass rounded-2xl p-5">
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Facturation</h3>
              <dl>
                <Row label="N° TVA" value={client.vat_number} />
                <Row label="SIREN / EIN" value={client.registration_no} />
                <Row label="Email de facturation" value={client.billing_email} />
                <Row label="Adresse" value={[client.billing_address, client.billing_postal_code, client.billing_city].filter(Boolean).join(", ") || null} />
                <Row label="Devise" value={client.currency} />
                <Row label="Acompte par défaut" value={`${Number(client.deposit_pct)} %`} />
                <Row label="Délai de paiement" value={`${client.payment_days} jours`} />
                <Row label="Régime fiscal" value={client.tax_regimes ? `${client.tax_regimes.label} (${Number(client.tax_regimes.rate_pct)} %)` : null} />
              </dl>
            </section>
            <section className="glass rounded-2xl p-5">
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Livraison des leads</h3>
              <dl>
                <Row label="Canal" value={DELIVERY_CHANNEL_LABEL[client.delivery_channel]} />
                <Row label="Précisions" value={(cfg.note as string) || null} />
                <Row label="Site web" value={client.website ? <a href={client.website} target="_blank" rel="noreferrer" className="underline">{client.website}</a> : null} />
                <Row label="Créé le" value={dateShort(client.created_at)} />
              </dl>
              <div className="mt-6">
                <CustomFieldsView entity="clients" value={(client.custom_fields as Record<string, unknown>) ?? {}} />
              </div>
              {client.notes && (
                <div className="mt-6">
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Notes internes</h3>
                  <p className="whitespace-pre-wrap text-sm">{client.notes}</p>
                </div>
              )}
            </section>
          </div>
        </TabsContent>
        <TabsContent value="contacts"><ContactsTab clientId={client.id} /></TabsContent>
        <TabsContent value="prices"><PricesTab clientId={client.id} currency={client.currency} /></TabsContent>
        <TabsContent value="activity"><ActivityTab clientId={client.id} /></TabsContent>
        <TabsContent value="quotes"><ClientQuotesTab clientId={client.id} /></TabsContent>
        <TabsContent value="contracts"><ClientContractsTab clientId={client.id} /></TabsContent>
        <TabsContent value="orders"><ClientOrdersTab clientId={client.id} /></TabsContent>
        <TabsContent value="leads"><ClientLeadsTab clientId={client.id} /></TabsContent>
        <TabsContent value="invoices"><ClientInvoicesTab clientId={client.id} currency={client.currency} /></TabsContent>
      </Tabs>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto pb-0">
          <DialogHeader><DialogTitle>Modifier {client.legal_name}</DialogTitle></DialogHeader>
          <ClientForm client={client} onDone={() => setEditing(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={suspending} onOpenChange={setSuspending}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspendre les livraisons</DialogTitle>
            <DialogDescription>Le client reste visible et facturable, mais aucune livraison de leads ne pourra être enregistrée jusqu'à la levée de la suspension.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="reason">Motif *</Label>
            <Textarea id="reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. facture IC-2026-0012 impayée depuis 20 jours" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setSuspending(false)}>Annuler</Button>
            <Button variant="destructive" onClick={suspend} disabled={save.isPending}>Suspendre</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
